import {
  Component,
  Input,
  EventEmitter,
  Output,
  SimpleChanges,
  OnChanges,
  ChangeDetectionStrategy
} from "@angular/core";
import { FiltersData } from "../../../../services/filters.service";
import { IGraph } from "../graph-config";
import { analyticsConfigFor } from "../../../../services/filter-sync-configs";
import { SvgIconComponent } from "angular-svg-icon";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "../../../elements/button/button.component";
import { FilterControlComponent } from "../../../elements/filter-control/filter-control.component";
import { SearchbarNumberComponent } from "../../../elements/searchbar-number/searchbar-number.component";
import { TranslateModule } from "@ngx-translate/core";

/** The kinds of control the header knows how to render, one template slot each. */
type AnalyticsControlKind = "checkbox" | "radio" | "dateRange" | "number";

/**
 * One filter control of the analytics header: everything that varies from one
 * control to the next, including where its current value comes from and where a
 * change goes.
 *
 * The header used to spell each of these out as its own block of markup gated on
 * `@if (graphConfig?.frontConfig?.<flag>)`. Nine blocks meant the chart-to-control
 * mapping was written twice — once here as a flag test, once in
 * ANALYTICS_PARAM_FLAGS as the same flag against a URL param — and the two could
 * drift: a chart that declared `packaging` while the header forgot its gate got a
 * `pack` param in the URL with no way to change it. Now the header asks
 * `analyticsConfigFor()` which params this chart declares and renders the matching
 * rows, so the flags are named in exactly one place.
 *
 * The value and the emit are closures rather than plain data because both read
 * `@Input()`s that change while the row stays the same. Passing the array through
 * unchanged matters: a `checkbox` control in staged mode edits `checkedData` in
 * place, so `checked()` must hand back the field itself, not a copy.
 */
interface AnalyticsControl {
  /** The ANALYTICS_FILTER_CONFIG param this control edits (its identity here). */
  readonly paramKey: string;
  /** Which template slot renders it. */
  readonly kind: AnalyticsControlKind;
  /** Filter label, which is also the key the control fetches its options under. */
  readonly label: string;
  readonly icon: string;
  /**
   * The control is built from the response, so it can only be shown once data has
   * landed — the four site/packaging filters, whose option lists are extracted
   * from the chart's own data.
   */
  readonly needsData?: boolean;
  /** `checkbox`: offer the all-or-nothing variant. */
  readonly showAllVariant?: boolean;
  /** `radio`: i18n path for the option labels, with `{{item}}` for the value. */
  readonly pathTranslate?: string;
  /** `checkbox`: cap on the number of selected items, when the chart sets one. */
  readonly maxItems?: () => number | undefined;
  /** `checkbox`: options, when they come from the response instead of the API. */
  readonly options?: () => FiltersData[];
  /** `checkbox`: the selected values, by reference. */
  readonly checked?: () => Array<string | number>;
  /** `radio`: the options. */
  readonly radioOptions?: () => readonly string[];
  /** `radio`: the selected option. */
  readonly radioValue?: () => string | undefined;
  /** Where a change goes: the matching `@Output()`. */
  readonly emit: (value: any) => void;
}

@Component({
  selector: "app-header-analytics",
  templateUrl: "./header-analytics.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./header-analytics.component.scss"],
  imports: [
    SvgIconComponent,
    FormsModule,
    ButtonComponent,
    FilterControlComponent,
    SearchbarNumberComponent,
    TranslateModule
  ]
})
export class HeaderAnalyticsComponent implements OnChanges {
  @Output() departureSites: EventEmitter<string[]> = new EventEmitter();
  @Output() arrivalSites: EventEmitter<string[]> = new EventEmitter();
  @Output() containers: EventEmitter<string[]> = new EventEmitter();
  @Output() sites: EventEmitter<string[]> = new EventEmitter();
  @Output() dates: EventEmitter<Date[]> = new EventEmitter();
  @Output() days: EventEmitter<number> = new EventEmitter();
  @Output() alertType: EventEmitter<string> = new EventEmitter();
  @Output() containerCategory: EventEmitter<string> = new EventEmitter();
  @Output() packaging: EventEmitter<string[]> = new EventEmitter();
  @Output() goToLibrary: EventEmitter<void> = new EventEmitter();
  @Output() searchTermChanged: EventEmitter<string> = new EventEmitter();

  @Input() hasData: boolean = false;
  @Input() isGraphSelected: boolean = false;
  @Input() showGraphLibrary: boolean = false;
  @Input() searchTerm: string = "";

  // Regular filter values
  @Input() filterContainers: string[] = [];
  @Input() filterSites: string[] = [];
  @Input() filterDepartureSites: string[] = [];
  @Input() filterArrivalSites: string[] = [];
  @Input() filterDateStart: Date | undefined;
  @Input() filterDateEnd: Date | undefined;
  @Input() filterDays: number | undefined;
  @Input() filterAlertType: string | undefined;
  @Input() filterContainerCategory: string | undefined;
  @Input() filterPackaging: string[] = [];

  // Filter options from API data
  @Input() filterSitesInput: FiltersData[] = [];
  @Input() filterDepartureSitesInput: FiltersData[] = [];
  @Input() filterArrivalSitesInput: FiltersData[] = [];
  @Input() alertTypesInput: readonly string[] = [];
  @Input() containerCategoriesInput: readonly string[] = [];
  @Input() filterPackagingInput: FiltersData[] = [];

  @Input() graphConfig: IGraph;

  public filterDates: Date[] = [];

  /**
   * Every control the header can render, in the order they are shown. The order
   * lives here rather than in ANALYTICS_FILTER_CONFIG because it is a display
   * decision: that config is ordered for the URL, and reading its order would
   * silently reshuffle the header.
   */
  private readonly controls: readonly AnalyticsControl[] = [
    {
      paramKey: "pkg",
      kind: "checkbox",
      label: "containerType",
      icon: "container",
      maxItems: () => this.graphConfig?.frontConfig?.maxContainers || undefined,
      checked: () => this.filterContainers,
      emit: (value: string[]) => this.containers.emit(value)
    },
    {
      paramKey: "range",
      kind: "dateRange",
      label: "dateRange",
      icon: "slider",
      emit: (value: Date[]) => this.dates.emit(value)
    },
    {
      paramKey: "span",
      kind: "number",
      label: "days",
      icon: "calendar",
      emit: (value: number) => this.days.emit(value)
    },
    {
      paramKey: "cat",
      kind: "radio",
      label: "containerCategories",
      icon: "pallet",
      pathTranslate: "filter.containerCategories.label.{{item}}",
      radioOptions: () => this.containerCategoriesInput,
      radioValue: () => this.filterContainerCategory,
      emit: (value: string) => this.containerCategory.emit(value)
    },
    // alertTypes is an optional cache filter that acts like a main one.
    {
      paramKey: "alert",
      kind: "radio",
      label: "alertTypes",
      icon: "warn",
      pathTranslate: "filter.alertTypes.label.{{item}}",
      radioOptions: () => this.alertTypesInput,
      radioValue: () => this.filterAlertType,
      emit: (value: string) => this.alertType.emit(value)
    },
    {
      paramKey: "sites",
      kind: "checkbox",
      label: "sites",
      icon: "site",
      needsData: true,
      showAllVariant: true,
      options: () => this.filterSitesInput,
      checked: () => this.filterSites,
      emit: (value: string[]) => this.sites.emit(value)
    },
    {
      paramKey: "dep",
      kind: "checkbox",
      label: "departureSites",
      icon: "last-site",
      needsData: true,
      options: () => this.filterDepartureSitesInput,
      checked: () => this.filterDepartureSites,
      emit: (value: string[]) => this.departureSites.emit(value)
    },
    {
      paramKey: "arr",
      kind: "checkbox",
      label: "arrivalSites",
      icon: "next-site",
      needsData: true,
      options: () => this.filterArrivalSitesInput,
      checked: () => this.filterArrivalSites,
      emit: (value: string[]) => this.arrivalSites.emit(value)
    },
    {
      paramKey: "pack",
      kind: "checkbox",
      label: "packaging",
      icon: "container",
      needsData: true,
      showAllVariant: true,
      options: () => this.filterPackagingInput,
      checked: () => this.filterPackaging,
      emit: (value: string[]) => this.packaging.emit(value)
    }
  ];

  /** The controls the selected chart declares and has the data for. */
  protected shownControls: readonly AnalyticsControl[] = [];

  constructor() {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterDateStart || changes.filterDateEnd) {
      this.filterDates = [this.filterDateStart, this.filterDateEnd].filter(
        (d): d is Date => d !== undefined
      );
    }
    // Both are @Inputs, so this runs exactly when the old per-control @if
    // conditions could have changed value.
    if (changes.graphConfig || changes.hasData) {
      this.shownControls = this.visibleControls();
    }
  }

  /**
   * The controls to render: the params this chart declares (the single mapping,
   * ANALYTICS_PARAM_FLAGS, resolved by analyticsConfigFor) minus the ones built
   * from a response that has not landed.
   * @returns {readonly AnalyticsControl[]} Rows to render, in display order.
   */
  private visibleControls(): readonly AnalyticsControl[] {
    const declared: Set<string> = new Set<string>(
      analyticsConfigFor(this.graphConfig?.frontConfig).map(
        (entry) => entry.paramKey
      )
    );
    return this.controls.filter(
      (control) =>
        declared.has(control.paramKey) && (!control.needsData || this.hasData)
    );
  }

  public navigateToLibrary(): void {
    this.goToLibrary.emit();
  }

  public onSearchChanged(searchTerm: string): void {
    this.searchTermChanged.emit(searchTerm);
  }

  public clearSearch(): void {
    this.searchTermChanged.emit("");
  }
}
